import persistent from '../data/persistent.svelte.js';
import { currentSession } from '../data/session.svelte.js';
import configuration from '../configuration.json';

/**
 * In-memory storage for Google Reserve tokens
 * Structure: { 'reserve-client-salon': { token: string, expires: timestamp } }
 */
let tokenCache = $state({});

/**
 * Storage key prefix for localStorage
 */
const STORAGE_KEY_PREFIX = 'reserve-';

/**
 * Token expiration time (30 days in milliseconds)
 */
const TOKEN_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Generate storage key for a client-salon combination
 */
function getStorageKey(client, salon) {
    return `${STORAGE_KEY_PREFIX}${client}-${salon}`;
}

/**
 * Load token from localStorage into memory cache
 */
function loadTokenFromStorage(client, salon) {
    try {
        const key = getStorageKey(client, salon);
        const stored = localStorage.getItem(key);
        if (stored) {
            const tokenData = JSON.parse(stored);
            // Check if token is still valid
            if (tokenData.expires > Date.now()) {
                tokenCache[key] = tokenData;
            } else {
                // Clean up expired token
                localStorage.removeItem(key);
            }
        }
    } catch (error) {
        console.error('Failed to load Google Reserve token from storage:', error);
    }
}

/**
 * Save token to localStorage
 */
function saveTokenToStorage(client, salon) {
    try {
        const key = getStorageKey(client, salon);
        if (tokenCache[key]) {
            localStorage.setItem(key, JSON.stringify(tokenCache[key]));
        }
    } catch (error) {
        console.error('Failed to save Google Reserve token to storage:', error);
    }
}

/**
 * Watch for cookie consent changes to persist tokens when consent is granted
 */
$effect.root(() => {
    $effect(() => {
        if (persistent.cookies && Object.keys(tokenCache).length > 0) {
            // Save all tokens in cache to localStorage when consent is granted
            Object.keys(tokenCache).forEach(key => {
                const match = key.match(/^reserve-(.+)-(.+)$/);
                if (match) {
                    const [, client, salon] = match;
                    saveTokenToStorage(client, salon);
                }
            });
        }
    });
});

/**
 * Store a Google Reserve token for a specific client-salon combination
 * (Internal function)
 *
 * @param {string} client - The client ID
 * @param {string} salon - The salon ID
 * @param {string} token - The Google Reserve token
 */
function storeToken(client, salon, token) {
    if (!client || !salon || !token) {
        console.error('Invalid parameters for storeToken:', { client, salon, token });
        return;
    }

    const key = getStorageKey(client, salon);
    const expires = Date.now() + TOKEN_EXPIRATION_MS;

    // Always store in memory
    tokenCache[key] = {
        token,
        expires
    };

    // Store in localStorage if we have cookie consent
    if (persistent.cookies) {
        saveTokenToStorage(client, salon);
    }
}

/**
 * Retrieve a Google Reserve token for a specific client-salon combination
 * (Internal function)
 * Returns null if token doesn't exist or has expired
 *
 * @param {string} client - The client ID
 * @param {string} salon - The salon ID
 * @returns {string|null} The token if valid, null otherwise
 */
function retrieveToken(client, salon) {
    if (!client || !salon) {
        console.error('Invalid parameters for retrieveToken:', { client, salon });
        return null;
    }

    const key = getStorageKey(client, salon);

    // Try to load from localStorage if we have consent and it's not in cache
    if (persistent.cookies && !tokenCache[key]) {
        loadTokenFromStorage(client, salon);
    }

    const tokenData = tokenCache[key];

    if (!tokenData) {
        return null;
    }

    const now = Date.now();

    // Check if token has expired
    if (tokenData.expires <= now) {
        // Clean up expired token
        delete tokenCache[key];
        if (persistent.cookies) {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                console.error('Failed to remove expired token:', error);
            }
        }
        return null;
    }

    return tokenData.token;
}

/**
 * Initialize Google Reserve with a token from the URL
 * Stores the token for the current session's client-salon combination
 *
 * @param {string} token - The Google Reserve token from URL parameter
 */
export function initializeReserve(token) {
    if (!token) {
        console.error('No token provided to initializeReserve');
        return;
    }

    if (!currentSession?.client || !currentSession?.salon) {
        console.error('Cannot initialize Google Reserve: session client/salon not available');
        return;
    }

    // Set the session origin to indicate this booking came from Google Reserve
    currentSession.origin = 'google-reserve';

    storeToken(currentSession.client, currentSession.salon, token);
}

/**
 * Register a conversion with Google Reserve after successful appointment booking
 * Retrieves the stored token and submits it to Google's endpoint
 */
export async function registerConversionForReserve() {
    if (!currentSession?.client || !currentSession?.salon) {
        console.error('Cannot register conversion: session client/salon not available');
        return;
    }

    const token = retrieveToken(currentSession.client, currentSession.salon);

    if (!token) {
        console.log('No Google Reserve token found for this client-salon combination');
        return;
    }

    // Submit conversion to Google Reserve endpoint
    // Use debug endpoint when in test mode
    const isTestMode = configuration['reserve-test-mode'];
    const endpoint = isTestMode
        ? 'https://www.google.com/maps/conversion/debug/collect'
        : 'https://www.google.com/maps/conversion/collect';

    try {
        await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'text/plain'
            },
            body: JSON.stringify({
                conversion_partner_id: configuration['reserve-partner-id'],
                rwg_token: token,
                merchant_changed: 2
            })
        });

        console.log(`Google Reserve conversion submitted successfully (${isTestMode ? 'TEST MODE' : 'PRODUCTION'})`);
    } catch (error) {
        console.error('Failed to submit Google Reserve conversion:', error);
    }
}
